import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { ON_YOUR_HONOR_CONFIRMATIONS } from '../model/onYourHonor';
import { getInquiryV2QueryOptions, type GetInquiryV2Response } from '@/services/getInquiryV2';
import { useOnYourHonorStore } from '../state/onYourHonorStore';
import { updateInquiryV2 } from '@/services/updateInquiryV2';
import {
  trackOnYourHonorCancelled,
  trackOnYourHonorConfirmed,
  trackOnYourHonorIntroDismissed,
  trackOnYourHonorIntroViewed,
  trackOnYourHonorRemoved,
  trackOnYourHonorSubmitted,
  trackOnYourHonorTriggered,
  type OnYourHonorAnalyticsContext,
} from '@/util/analyticsEvents';
import { Sentry } from '@/util/sentry';

type UseOnYourHonorArgs = {
  conversationId: string;
  /** Server-canonical confirmed state, read from `inquiry.title.onYourHonor`. */
  confirmed: boolean;
  inquiryId?: string | number;
  recipientId?: string | number;
};

/** Result of a toggle: whether confirmed is now on, and the line to thank with if so. */
type ToggleResult = { enabled: boolean; thanksLine: string | null };

/**
 * Drives the expert "On your honor" toggle in the inquiry action bar.
 *
 * Honor-system self-attestation with no reason picker. The confirmed state is owned by the
 * server (`inquiry.title.onYourHonor`) and persisted through the inquiry PATCH,
 * so it follows the expert across devices. `confirmed` is that server value passed
 * in by the caller; `toggle` PATCHes the next state and optimistically flips the
 * single-inquiry query cache so the toggle reacts immediately, reconciling with
 * the server on settle. On the off→on press it also advances the in-memory
 * rotation and returns a gratitude line; the caller surfaces it as the reason
 * sheet's thank-you heading (so each confirmation thanks with a different line
 * rather than repeating).
 *
 * `acknowledgeSent` is called from the send flow for analytics only: when the
 * inquiry is confirmed at send time it records the submission. It reads the live
 * cache (not a closed-over value) and does not clear the confirmation.
 *
 * The tracking callbacks list `analyticsContext`'s individual fields as deps
 * rather than the object (which is rebuilt every render); the `exhaustive-deps`
 * disable on each is for that, not a missing dependency.
 */
export function useOnYourHonor({
  conversationId,
  confirmed,
  inquiryId,
  recipientId,
}: UseOnYourHonorArgs) {
  const queryClient = useQueryClient();

  const advanceConfirmation = useOnYourHonorStore((state) => state.advanceConfirmation);

  const registerSubmission = useOnYourHonorStore((state) => state.registerSubmission);

  const analyticsContext: OnYourHonorAnalyticsContext = { conversationId, inquiryId, recipientId };

  const queryKey = useMemo(
    () => getInquiryV2QueryOptions({ conversationId }).queryKey,
    [conversationId]
  );

  /**
   * Persists the confirmation and optimistically writes it into the single-inquiry
   * cache so the toggle reflects the change before the request resolves; rolls
   * the cache back on error and reconciles with the server on settle.
   */
  const { mutate: persistMark } = useMutation({
    mutationFn: (next: boolean) => updateInquiryV2({ conversationId, onYourHonor: next }),
    onMutate: async (next) => {
      // Cancel in-flight refetches first; otherwise one can resolve after this
      // optimistic write and clobber it back to the stale server value.
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<GetInquiryV2Response>(queryKey);

      queryClient.setQueryData<GetInquiryV2Response>(queryKey, (prev) =>
        prev ? { ...prev, title: { ...prev.title, onYourHonor: next } } : prev
      );

      return { previous };
    },
    onError: (error, _next, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }

      Sentry.captureException(error, {
        tags: { component: 'useOnYourHonor', source: 'persist-mark' },
        extra: { conversationId },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey }).catch(() => undefined);
    },
  });

  /**
   * Confirms the mark, persists it, tracks the change, and returns the next enabled
   * state plus the rotating gratitude line to head the reason sheet with on the
   * off→on press (null
   * when the confirmation is off). `salutation` (e.g. "Dr. Lin", or "" when unknown)
   * is woven into the line; the caller passes the live value at press time so it
   * isn't captured stale.
   */
  const toggle = useCallback(
    (salutation = ''): ToggleResult => {
      const nextEnabled = !confirmed;

      persistMark(nextEnabled);

      if (!nextEnabled) {
        trackOnYourHonorRemoved(analyticsContext);

        return { enabled: false, thanksLine: null };
      }

      trackOnYourHonorConfirmed(analyticsContext);

      const index = advanceConfirmation();

      return {
        enabled: true,
        thanksLine:
          ON_YOUR_HONOR_CONFIRMATIONS[index % ON_YOUR_HONOR_CONFIRMATIONS.length](salutation),
      };
    },
    [confirmed, persistMark, advanceConfirmation, conversationId, inquiryId, recipientId] // eslint-disable-line react-hooks/exhaustive-deps
  );

  /**
   * Called from the message-send flow for analytics only. Reads the confirmation live
   * from the cache (not a closed-over value) so it reflects the toggle at send
   * time; when the inquiry is confirmed it records the submission once per inquiry
   * (deduped through the store so repeated sends don't inflate the count). Does
   * not clear the confirmation.
   */
  const acknowledgeSent = useCallback((): void => {
    const current = queryClient.getQueryData<GetInquiryV2Response>(queryKey);

    if (!current?.title?.onYourHonor) {
      return;
    }

    if (!registerSubmission(conversationId)) {
      return;
    }

    trackOnYourHonorSubmitted(analyticsContext);
  }, [queryClient, queryKey, registerSubmission, conversationId, inquiryId, recipientId]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Records the expert tapping the On Your Honor button, opening the inline confirm. */
  const trackTriggered = useCallback(
    (isConfirmed: boolean): void => {
      trackOnYourHonorTriggered({ ...analyticsContext, isMarked: isConfirmed });
    },
    [conversationId, inquiryId, recipientId]
  ); // eslint-disable-line react-hooks/exhaustive-deps

  /** Records backing out of the inline confirm (no confirmation made). */
  const trackConfirmCancelled = useCallback((): void => {
    trackOnYourHonorCancelled(analyticsContext);
  }, [conversationId, inquiryId, recipientId]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Records the one-time intro banner becoming visible. */
  const trackIntroViewed = useCallback((): void => {
    trackOnYourHonorIntroViewed(analyticsContext);
  }, [conversationId, inquiryId, recipientId]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Records the expert dismissing the one-time intro banner. */
  const trackIntroDismissed = useCallback((): void => {
    trackOnYourHonorIntroDismissed(analyticsContext);
  }, [conversationId, inquiryId, recipientId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    isConfirmed: confirmed,
    toggle,
    acknowledgeSent,
    trackTriggered,
    trackConfirmCancelled,
    trackIntroViewed,
    trackIntroDismissed,
  };
}
